import { useCallback, useRef, useSyncExternalStore } from "react";
import type { StreamState } from "./stream-state";
import type { StreamStore } from "./use-density-stream";

export interface SelectorCache<T> {
  state: StreamState;
  selector: (state: StreamState) => T;
  value: T;
}

/** Подписка, которая никого не будит: на паузе компонент не слушает поток. */
const NO_SUBSCRIPTION = (): (() => void) => () => {};

/**
 * Подписка на хранилище с учётом паузы. Ссылка на возвращаемую функцию
 * постоянна для каждого из двух состояний: смена паузы переподписывает
 * `useSyncExternalStore`, и при снятии паузы он сам перечитывает снимок.
 */
export function subscriptionFor(store: StreamStore, paused: boolean): StreamStore["subscribe"] {
  return paused ? NO_SUBSCRIPTION : store.subscribe;
}

/**
 * Чтение среза с кэшем. На паузе возвращается значение из кэша, даже если
 * состояние потока ушло вперёд: компонент, который не слушает поток, не должен
 * получать от чтения новую ссылку. Пустой кэш (первый рендер) на паузе
 * считается как обычно. Равный по `isEqual` результат сохраняет прежнюю ссылку.
 */
export function readSelector<T>(
  cache: { current: SelectorCache<T> | null },
  state: StreamState,
  selector: (state: StreamState) => T,
  isEqual: (previous: T, next: T) => boolean,
  paused: boolean,
): T {
  const cached = cache.current;
  if (cached !== null && cached.selector === selector) {
    if (paused || cached.state === state) {
      return cached.value;
    }
  }
  const next = selector(state);
  const value = cached !== null && isEqual(cached.value, next) ? cached.value : next;
  cache.current = { state, selector, value };
  return value;
}

/**
 * Срез состояния потока для компонента. Компонент перерисовывается, только
 * когда результат селектора изменился по `isEqual`; при равенстве возвращается
 * прежняя ссылка, так что производный массив, пересчитанный на каждый кадр,
 * не будит мемоизированных потомков. Селектор должен быть стабильной ссылкой
 * (функция уровня модуля или `useCallback`).
 *
 * `paused` — компонент скрыт и поток ему не нужен: подписки нет, значение
 * держится прежним. Со снятием паузы компонент один раз догоняет текущее
 * состояние.
 */
export function useStreamSelector<T>(
  store: StreamStore,
  selector: (state: StreamState) => T,
  isEqual: (previous: T, next: T) => boolean = Object.is,
  paused = false,
): T {
  const cache = useRef<SelectorCache<T> | null>(null);
  const getSnapshot = useCallback(
    (): T => readSelector(cache, store.getSnapshot(), selector, isEqual, paused),
    [store, selector, isEqual, paused],
  );
  return useSyncExternalStore(subscriptionFor(store, paused), getSnapshot, getSnapshot);
}
